import { useCallback, useEffect, useMemo } from 'react'
import {
  createTreeStore,
  no_parent,
  NO_PARENT,
  node_id,
  TreeData,
  TreeItem,
  TreeNode,
  TreeStore,
  TreeStoreContext,
  useTreeStoreInstance,
} from '../widgets/TreePlusPlus/model'
import { TreeConfigContext, TreePlusPlusConfigOptions } from '../widgets/TreePlusPlus/config'
import { TreeNodesViewRoot } from '../widgets/TreePlusPlus/view/tree-nodes'
import { store, useDispatch, useSelector } from '@/store'
import { WidgetConfig } from '../WidgetConfiguration'
import { ViewerConfig } from "../Viewer"
import { useHostedViewerCtx } from "@/hooks/use-app-ctx"
import { Instruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item'
import { allWidgetDefinitionsSorted } from '../widgets'
import { WidgetDescription } from '@/helpers/widget'
import { useWidgetSelectorStore } from './widget-selector/widget-selector'
import { saveViewerSnapshot } from '@/components/editor/widget-editor/undo-redo'
import { revealSignal } from './widget-editor/reveal'
import { toast } from 'sonner'
import { PanelChrome } from '@/components/editor/panels/panel-chrome'
import { Crosshair } from 'lucide-react'
import { TreePerRowNativeJob } from "@/components/widgets/TreePlusPlus/mutation/tree-action-dispatch"
import { usePanelRegistry } from "@/components/editor/panels/panel-registry"
import { useSettingsOnWidgetStore, WIDGET_EDIT_PANEL } from "@/components/editor/widget-editor/settings-size"
import { Signal } from "@/utils/signals"

// happy-todo: split up this big file

// random comment to trigger CI

const widgetTreeStore = createTreeStore()

export function WidgetTree() {
  const viewerConfig = useSelector(s => s.currentViewer.config as unknown as ViewerConfig)
  const dispatch = useDispatch()

  const handleDispatch = useCallback((node: TreeNode, jobs: TreePerRowNativeJob[]) => {
    if (jobs.length !== 1) throw new Error("Expected a single job")
    const job = jobs[0]
    switch (job.action) {
      case "edit-widget":
        useWidgetSelectorStore.setState({ selectedPath: node.id })
        // the settings show on the widget once it is selected
        if (!useSettingsOnWidgetStore.getState().onWidget) usePanelRegistry.getState().openPanel(WIDGET_EDIT_PANEL)
        return
      case "select-widget":
        useWidgetSelectorStore.setState({ selectedPath: node.id })
        return
      case "reveal-widget":
        revealSignal.emit({ path: node.id, mainArea: viewerConfig.mainArea })
        return
      case "delete-widget":
        handleDeleteWidget(viewerConfig, node.id)
        return
    }
  }, [ viewerConfig ])

  const handleMouseEnter = useCallback((node: TreeNode) => {
    useWidgetSelectorStore.setState({ hoveredPath: node.id })
  }, [])

  const handleMouseLeave = useCallback(() => {
    useWidgetSelectorStore.setState({ hoveredPath: null })
  }, [])

  const handleDrop = useCallback((instruction: Instruction, source: node_id, target: node_id) => {
    handleInstruction(viewerConfig, dispatch, instruction, source, target)
  }, [ viewerConfig, dispatch ])

  const treeConfig = useMemo<TreePlusPlusConfigOptions>(() => {
    return {
      selectMode: "single",
      variant: "compact",
      dndEnabled: true,
      dndReparentEnabled: true,
      dndReorderEnabled: true,
      sparqlEndpoint: "NOT_USED",
      buttonsConfig: {
        perRow: [{
          label: "Widget actions",
          icon: "lucide-ellipsis",
          buttons: [{
            icon: "lucide-pencil",
            label: "Edit",
            actions: [{ type: "native", action: "edit-widget" }]
          }, {
            label: "Select",
            actions: [{ type: "native", action: "select-widget" }]
          }, {
            label: "Reveal",
            actions: [{ type: "native", action: "reveal-widget" }]
          }, {
            separator: true
          }, {
            icon: "lucide-trash",
            label: "Remove",
            actions: [{ type: "native", action: "delete-widget" }]
          }]
        }],
        noRow: []
      },
      native: {
        popoverContentClasses: "text-xs font-exo font-[Exo_2_Variable]",
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onDrop: handleDrop,
        dispatch: {
          perRow: handleDispatch
        }
      }
    } satisfies TreePlusPlusConfigOptions
  }, [ handleMouseEnter, handleMouseLeave, handleDrop, handleDispatch ]) // happy-todo: this is ugly

  return (
    <TreeStoreContext.Provider value={widgetTreeStore}>
      <TreeConfigContext.Provider value={treeConfig}>
        <WidgetTreeView />
      </TreeConfigContext.Provider>
    </TreeStoreContext.Provider>
  )
}

function WidgetTreeView() {
  const useTreeStore = useTreeStoreInstance()
  const treeData = useTreeStore(st => st.treeData)
  const lastSelectedNode = useTreeStore(st => st.client.lastSelectedNode)
  const viewerConfig = useSelector(s => s.currentViewer.config as unknown as ViewerConfig)
  const { widgetSelectorRef } = useHostedViewerCtx()
  const dispatch = useDispatch()

  useEffect(() => {
    syncTreeDataFromWidgets(useTreeStore, viewerConfig)
  }, [ viewerConfig ])

  useEffect(() => {
    if (!lastSelectedNode) return
    useWidgetSelectorStore.setState({
      selectedPath: lastSelectedNode
    })
  }, [ lastSelectedNode ])

  useEffect(() => {
    const widgetSelector = widgetSelectorRef.current
    if (!widgetSelector) return
    widgetSelector.selectedSignal.connect(reveal)
    return () => widgetSelector.selectedSignal.disconnect(reveal)
  }, [ widgetSelectorRef ])

  return (
    <div className="p-[1px] font-exo">
      <TreeNodesViewRoot treeData={treeData} />
    </div>
  )
}

export function WidgetTreeHeaderIcons() {
  const selectedPath = useWidgetSelectorStore(s => s.selectedPath)
  return (
    <PanelChrome.HeaderIcon
      icon={Crosshair}
      disabled={!selectedPath}
      onClick={() => selectedPath && reveal(selectedPath)}
      title="Reveal selected widget in tree" />
  )
}

function reveal(selectedPath: string) {
  const state = widgetTreeStore.getState()
  if (!(selectedPath in state.treeData.itemsById)) {
    console.log("zzz niet", selectedPath, state.treeData)

    const viewerConfig = store.getState().currentViewer.config
    if (!viewerConfig) throw new Error("No current viewer config so cannot add new widget")

    // happy-todo: better confirmation dialog
    if (!window.confirm(`Create new widget ${selectedPath} here?`)) return
    handleAddWidget(viewerConfig, selectedPath)

    onNextTreeSync.connect(() => {
      const newState = widgetTreeStore.getState()
      if (!(selectedPath in newState.treeData.itemsById)) {
        console.error(`Expected ${selectedPath} in tree`)
        return
      }
      reveal(selectedPath)
    })
  } else {
    state.actions.revealNode(selectedPath, "smooth")
    state.actions.select(selectedPath, "single")
  }
}

const onNextTreeSync = new Signal<void>()

function syncTreeDataFromWidgets(treeStore: TreeStore, viewerConfig: ViewerConfig) {
  treeStore.setState({
    treeData: createTreeDataFromWidgets(viewerConfig)
  })
  treeStore.getState().actions.computeVisibleNodeIds()
  treeStore.getState().actions.computeCheckState()
  treeStore.getState().actions.toggleNodeExpanded("mainArea", true)
  onNextTreeSync.emit()
  onNextTreeSync.clear()
}

function createTreeDataFromWidgets(viewerConfig: ViewerConfig): TreeData {
  const data: TreeData = {
    rootNodeIds: [],
    nodesById: {},
    itemsById: {}
  }
  function addArea(
    id: node_id,
    area: WidgetConfig | null,
    parentArea: WidgetConfig | null,
    parentId: node_id | no_parent) {
    const widgetDefinition = allWidgetDefinitionsSorted
      .find(w => w.name === area?.widget) as WidgetDescription<any, any, any> | undefined
    const icon
      = typeof widgetDefinition?.icon === "function"
      ? widgetDefinition.icon(area)
      : widgetDefinition?.icon
        ?? (area?.widget ? "lucide-box" : "lucide-square-dashed")

    const item: TreeItem = {
      id,
      uri: id,
      label: {
        text: area ? intelligentlyDecideStartLabel(area, parentArea) : "Empty widget"
      },
      icon: {
        name: icon
      },
      rightLabel: {
        text: area ? intelligentlyDecideEndLabel(area, parentArea) : undefined
      },
      rightIcon: {},
      defaultChecked: false,
      defaultToggles: {},
      rowResults: {},
      canDropInto: Boolean(widgetDefinition?.hasChildAreas)
    }
    const node: TreeNode = {
      id,
      itemId: id,
      childrenIds: [],
      parentId
    }

    data.itemsById[id] = item
    data.nodesById[id] = node
    for (const [i, child] of area?.areas?.entries() ?? []) {
      const newPath = stringifyIndicesToWidgetPath([ ...widgetPathToIndices(id), i ])
      node.childrenIds.push(addArea(newPath, child ?? null, area ?? null, id))
    }
    return id
  }
  const rootId = addArea("mainArea", viewerConfig.mainArea, null, NO_PARENT)
  data.rootNodeIds = [ rootId ]
  return data
}

export function getWidgetIcon(area: WidgetConfig) {
  const widgetDefinition = allWidgetDefinitionsSorted
    .find(w => w.name === area.widget) as WidgetDescription<any, any, any> | undefined
  const icon
    = typeof widgetDefinition?.icon === "function"
    ? widgetDefinition.icon(area)
    : widgetDefinition?.icon ?? "lucide-box"
  return icon
}

export function intelligentlyDecideEndLabel(area: WidgetConfig, parent: WidgetConfig | null) {
  const areaAsAny = area as any
  // Areas addressed by id (Toolbar dialogs and perspectives) are created before a widget is picked for them
  if (!area.widget && isIdAddressedArea(area)) return "empty"
  return area.widget
}

export function intelligentlyDecideStartLabel(area: WidgetConfig, parent: WidgetConfig | null) {
  if (parent === null) return "Root"
  const index = parent.areas?.indexOf(area)
  const parentAsAny = parent as any

  if (area.titleMenubar) {
    return area.titleMenubar
  }

  if (parent && parent.widget === "BBSideMenu" && Array.isArray(parentAsAny.tabs)) {
    if (index !== undefined && index in parentAsAny.tabs) return parentAsAny.tabs[index]
  }

  // Areas addressed by id (Toolbar dialogs and perspectives) are identified by that id, not by their position
  if (isIdAddressedArea(area)) return area.id || "(default area)"

  return area.widget ?? "Empty widget"
}

/** Areas created through `createAreaWithId`, which are looked up by their id instead of their index */
function isIdAddressedArea(area: WidgetConfig) {
  return typeof area.id === "string"
}

export function determineWidgetPath(widget: WidgetConfig, mainArea: WidgetConfig | null | undefined): string | null {
  if (!mainArea) return null
  if (widget === mainArea) return "mainArea"

  const path = findWidgetPath(widget, mainArea)
  if (!path) return null

  return stringifyIndicesToWidgetPath(path)

  function findWidgetPath(
    target: WidgetConfig,
    current: WidgetConfig,
    acc: number[] = []
  ): number[] | null {
    if (!current.areas) return null

    for (let i = 0; i < current.areas.length; i++) {
      const child = current.areas[i]
      if (!child) continue

      if (child === target) return [...acc, i]
      const nested = findWidgetPath(target, child, [...acc, i])
      if (nested) return nested
    }

    return null
  }
}

export function widgetPathToIndices(path: string): number[] {
  // "mainArea/0/1/2" → [0, 1, 2]
  return path
    .split('/')
    .slice(1)
    .map(Number)
    .filter(n => !Number.isNaN(n))
}

export function stringifyIndicesToWidgetPath(parsedPath: number[]) {
  if (parsedPath.length === 0) return "mainArea"
  return `mainArea/${parsedPath.join('/')}`
}

export function parentWidgetPath(path: string) {
  const parsedPath = widgetPathToIndices(path)
  if (parsedPath.length === 0) return null
  return stringifyIndicesToWidgetPath(parsedPath.slice(0, -1))
}

// happy-todo: use node_id not string

type MaybeWidgetConfig = WidgetConfig | null | undefined

export function handleAddWidget(
  currentViewer: ViewerConfig,
  targetPath: string
) {
  const pathIndices = widgetPathToIndices(targetPath)
  if (pathIndices.length === 0) {
    toast.error("Cannot add a root widget")
    return
  }

  const viewer: ViewerConfig = JSON.parse(JSON.stringify(currentViewer))
  const parentPath = parentWidgetPath(targetPath)
  if (!parentPath) throw new Error("No parent path found for path " + targetPath + ", this should not happen")

  const targetIndex = pathIndices[pathIndices.length - 1]

  const parentPathIndices = widgetPathToIndices(parentPath)
  if (parentPathIndices.length === 0) {
    if (!viewer.mainArea.areas) viewer.mainArea.areas = []
    viewer.mainArea.areas[targetIndex] = {} as WidgetConfig
  } else {
    const [ ,, parentArea ] = getAreaAndParentByPath(parentPathIndices, viewer.mainArea)
    if (!parentArea) {
      toast.warning("Could not add a widget here because the parent has not been created yet")
      return
    }

    if (!parentArea.areas) parentArea.areas = []
    parentArea.areas![targetIndex] = {} as WidgetConfig
  }

  saveViewerSnapshot(viewer as ViewerConfig, "add widget")
}

function handleDeleteWidget(
  currentViewer: ViewerConfig,
  node: node_id
) {
  // happy-todo: better confirmation?
  if (!window.confirm("Are you sure you want to remove this widget?")) return

  const viewer: ViewerConfig = JSON.parse(JSON.stringify(currentViewer))
  const path = widgetPathToIndices(node)

  if (path.length === 0) {
    toast.warning("Cannot delete root node")
    return
  }

  const [
    parentArea,
    parentAreas,
    area,
    index
  ] = getAreaAndParentByPath(path, viewer.mainArea)

  parentAreas.splice(index, 1)
  saveViewerSnapshot(viewer as ViewerConfig, "delete widget")
}

function handleInstruction(
  currentViewer: ViewerConfig,
  dispatch: ReturnType<typeof useDispatch>,
  instruction: Instruction,
  source: node_id,
  target: node_id
) {
  if (instruction.blocked) {
    if (instruction.operation === "combine") {
      toast.warning("Drop operation failed: this widget can not have children")
    } else {
      toast.warning("Drop operation failed: cannot move this widget here")
    }
    return
  }
  const viewer = JSON.parse(JSON.stringify(currentViewer))

  const sourcePath = widgetPathToIndices(source)
  const targetPath = widgetPathToIndices(target)

  const [
    sourceParentArea,
    sourceParentAreas,
    sourceArea,
    sourceIndex
  ] = getAreaAndParentByPath(sourcePath, viewer.mainArea)

  const [
    targetParentArea,
    targetParentAreas,
    targetArea,
    targetIndex
  ] = getAreaAndParentByPath(targetPath, viewer.mainArea)

  switch (instruction.operation) {
    case "reorder-before": {
      const [moved] = sourceParentAreas.splice(sourceIndex, 1)

      const insertIndex =
        sourceParentAreas === targetParentAreas && sourceIndex < targetIndex
          ? targetIndex - 1
          : targetIndex

      targetParentAreas.splice(insertIndex, 0, moved)
      break
    }
    case "reorder-after": {
      const [moved] = sourceParentAreas.splice(sourceIndex, 1)

      const insertIndex =
        sourceParentAreas === targetParentAreas && sourceIndex < targetIndex
          ? targetIndex
          : targetIndex + 1

      targetParentAreas.splice(insertIndex, 0, moved)
      break
    }
    case "combine": {
      const [moved] = sourceParentAreas.splice(sourceIndex, 1)

      if (!targetArea) throw new Error("cannot drop into null area")
      targetArea.areas ??= []
      targetArea.areas.push(moved)
      break
    }
  }

  saveViewerSnapshot(viewer as ViewerConfig, "move widget")
}

function getAreaAndParentByPath(
  path: number[],
  parent: MaybeWidgetConfig
) {
  const [ head, ...rest ] = path
  if (head === undefined) throw new Error("empty path")
  if (!parent) throw new Error("no parent")
  if (!parent.areas) parent.areas = []
  const area = parent.areas[head]
  if (rest.length === 0) {
    // happy-todo: better for this to return object not array (since many often unused)
    return [ parent, parent.areas, area, head ] as const
  }
  return getAreaAndParentByPath(rest, area)
}

export function getAreaByWidgetPath(pathString: string, mainArea: WidgetConfig) {
  if (pathString === "mainArea") return mainArea
  const path = widgetPathToIndices(pathString)
  const [ ,, area ] = getAreaAndParentByPath(path, mainArea)
  return area
}

export function mutateAreaByWidgetPath(pathString: string, mainArea: WidgetConfig, replacementArea: WidgetConfig) {
  if (pathString === "mainArea") throw new Error("cannot be used to mutate mainArea")
  const path = widgetPathToIndices(pathString)
  const [ parent,,, areaIndex ] = getAreaAndParentByPath(path, mainArea)
  if (!parent.areas) throw new Error('this parent has no areas')
  parent.areas[areaIndex] = replacementArea
  return mainArea
}
